import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { DateRangeSelector, DateRangeType } from '@cokitchen/cokitchen-components';
import PageLoader from 'components/base/page-loader';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import moment from 'moment';
import { dateDifference, formatDate, handleApiError } from 'utils/functions';
import TransactionEmptyState from 'assets/images/transactions-empty-state.png';

type Props = {
  dateRange: DateRangeType;
  setDateRange: (r: DateRangeType) => void;
};

const TransactionChart = (props: Props) => {
  const { activeCompany } = useDashboardContext();
  const { dateRange, setDateRange } = props;
  const companyId: string = activeCompany?.id;

  const [chartData, setChartData] = useState([] as any);
  const [loading, setLoading] = useState(false);

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
  );

  useEffect(() => {
    getDashboardMonthlyStats();
  }, [dateRange, activeCompany]);

  const getDashboardMonthlyStats = async () => {
    if (!companyId) return;
    const { startDate: start_date, endDate: end_date } = dateRange.date;
    let interval = '';
    if (dateDifference(start_date, end_date) <= 1) {
      interval = 'hour';
    } else if (dateDifference(start_date, end_date) > 56) {
      interval = 'month';
    } else if (dateDifference(start_date, end_date) > 31) {
      interval = 'week';
    } else if (dateDifference(start_date, end_date) < 31) {
      interval = 'day';
    }
    setLoading(true);
    try {
      const res = await apiService.get('admin/dashboard-chart', {
        params: {
          start_date,
          end_date,
          company_id: companyId
        }
      });
      const transactions_chart = res?.data?.data?.transactions_chart;
      const labelsFormat = transactions_chart?.map((item: any) => new Date(item?.transaction_day));
      const labels = labelsFormat?.map((item: any) => {
        return `${moment(item).format('DD/MM')} ${interval === 'hour' ? formatDate(item) : ''}`;
      });
      setChartData({
        labels,
        datasets: [
          {
            fill: true,
            backgroundColor: '#FFEDF0',
            pointStyle: 'line',
            pointRadius: 1,
            borderWidth: 2,
            label: 'Spent',
            lineTension: 0.2,
            borderColor: '#E60A2B',
            data: transactions_chart?.map((item: any) => item.spent)
          }
        ]
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: {
          callback: function (value: any) {
            return value;
          }
        },
        grid: {
          display: false
        }
      },
      x: {
        border: { dash: [4, 4] }, // for the grid lines

        grid: {
          tickBorderDash: [2, 3] // also for the tick, if long enough
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#fff',
        bodyColor: '#2E3031',
        bodySpacing: 4,
        titleColor: '#6F7376',
        boxWidth: 174,
        boxHeight: 99,
        label: ''
      }
    }
  };

  return (
    <div className=''>
      <div className='flex justify-between mr-4'>
        <p className='mb-[32px] text-base font-medium'>Transactions Chart</p>
        <DateRangeSelector onChange={setDateRange} />
      </div>
      <div>
        {loading ? (
          <PageLoader />
        ) : chartData?.datasets?.length > 0 &&
          chartData?.datasets[0]?.data?.some((item: any) => Number(item) !== 0) ? (
          <Line id='chart' options={options} data={chartData ?? []} height={400} />
        ) : (
          <div className='flex flex-col items-center justify-center m-2'>
            <img src={TransactionEmptyState} alt='transactions-empty' className='w-[200px]' />
            <p className='text-[#6F7376] text-sm mt-2'>
              There&apos;s nothing here to view at the moment. Let&apos;s kick things into gear and
              take action.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionChart;
